/**
 * Copyright 2026, CS GROUP, https://www.cs-soprasteria.com/
 *
 * This file is part of AntFlow project.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { processesContext } from 'ogc-processes-components';
import { AntflowApiEndpoint } from '@/services/dataprovider';
import { Loader } from '@/components/Loader.tsx';

import { DEFAULT_AUTO_REFRESH_INTERVAL } from '@/constants/constants';
import { useInterval } from 'primereact/hooks';
import { Message } from 'primereact/message';
import { useTranslation } from 'react-i18next';
import { AntFlowJob } from '@/types/models';
import { JobDetail } from './JobDetailsPage/JobDetail';

type ErrorData = {
  config: Record<string, unknown>;
  message: string;
  response?: {
    data: {
      instance: string;
      status: number;
      title: string;
      type: string;
    };
  };
};

interface Props {
  setJobId?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setProcessId?: React.Dispatch<React.SetStateAction<string | undefined>>;
}
export const JobDetailPage: React.FC<Props> = ({ setJobId, setProcessId }) => {
  const { processesEndpoint } = useContext(processesContext)!;

  const { t } = useTranslation();
  const [data, setData] = useState<AntFlowJob | null>(null);
  const params = useParams();
  const jobId = params.id;

  const getJob = useQuery({
    queryKey: ['get_job_detail', jobId, processesEndpoint],
    queryFn: async () => {
      const endpoint = processesEndpoint as AntflowApiEndpoint;
      if (!jobId) {
        throw new Error('missing job id in query');
      }
      const job = (await endpoint.get_job(jobId)) as AntFlowJob;
      setData(job);
      return job;
    },
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    if (data) {
      setJobId?.(data.jobID);
      setProcessId?.(data.processID);
    }
    return () => {
      setJobId?.(undefined);
      setProcessId?.(undefined);
    };
  }, [setJobId, setProcessId, data]);

  useInterval(
    () => {
      getJob.refetch?.();
    },
    DEFAULT_AUTO_REFRESH_INTERVAL,
    data ? ['accepted', 'running'].includes(data?.status) : false
  );

  const errorData: ErrorData | null = useMemo(() => {
    if (getJob.error) {
      return getJob.error as unknown as ErrorData;
    }
    return getJob.error;
  }, [getJob.error]);

  return (
    <div className="text-left flex flex-column pt-2">
      {getJob.isPending ? <Loader /> : data && <JobDetail refreshState={getJob.refetch} job={data as AntFlowJob} />}

      {getJob.isError && (
        <Message
          severity="error"
          className="mt-6 text-sm w-full"
          content={
            <div className="text-left w-full p-2">
              <div className="mb-3">
                <span className="">{t(['notifs.error.description', ''])}</span>
              </div>

              <div className="mb-2">
                <span className="font-bold">URL:&nbsp;</span>
                <span className="">{errorData?.response?.data?.instance}</span>
              </div>

              <div className=" mb-1">
                <span className="font-semibold">{t(['notifs.error.title', 'Error'])}:&nbsp;</span>
                {errorData?.response?.data?.title}
              </div>
              <div>
                <span className="font-semibold mb-1">Message:&nbsp;</span>
                <span className="">{getJob.error?.message}</span>
              </div>
            </div>
          }
        />
      )}
    </div>
  );
};
